/**
 * Static seed / demo data extracted from AgentContext.
 *
 * These arrays are the initial values shown to the user before any backend
 * data is fetched.  They are plain data — no React, no side-effects.
 */

export const initialAgents = [
  {
    id: 'internet-reimbursement',
    name: 'Internet Reimbursement',
    icon: '🌐',
    status: 'active',
    description: 'Handles internet expense reimbursement claims, validates receipts, and processes monthly allowance payouts.',
    capabilities: ['Claim Submission', 'Receipt Validation', 'Reimbursement Tracking', 'Policy Compliance'],
    tasksCompleted: 1540,
    avgResponseTime: '0.8s',
    successRate: '99.1%',
    model: 'GPT-4o',
    lastActive: 'Just now',
    logs: [
      { time: '10:32:01', message: 'Processing reimbursement claim for March', type: 'info' },
      { time: '10:32:03', message: 'Receipt validated successfully', type: 'success' },
      { time: '10:32:04', message: 'Claim approved and forwarded to payroll', type: 'success' },
    ],
  },
  {
    id: 'governance-security',
    name: 'Governance & Security',
    icon: '🛡️',
    status: 'active',
    description: 'Manages governance policies, security compliance checks, access control, and regulatory adherence.',
    capabilities: ['Policy Enforcement', 'Access Management', 'Compliance Audits', 'Security Alerts'],
    tasksCompleted: 987,
    avgResponseTime: '1.0s',
    successRate: '99.5%',
    model: 'GPT-4o',
    lastActive: '1 min ago',
    logs: [
      { time: '10:30:00', message: 'Completed compliance audit for Q1', type: 'success' },
      { time: '10:31:00', message: 'Updated security policy documentation', type: 'info' },
    ],
  },
  {
    id: 'ezyclaim',
    name: 'EzyClaim',
    icon: '📋',
    status: 'active',
    description: 'Streamlines employee expense claims including travel, meals, and miscellaneous reimbursements.',
    capabilities: ['Expense Filing', 'Auto-Categorization', 'Approval Routing', 'Status Tracking'],
    tasksCompleted: 2310,
    avgResponseTime: '0.6s',
    successRate: '98.7%',
    model: 'GPT-4o',
    lastActive: 'Just now',
    logs: [
      { time: '10:31:45', message: 'New travel claim submitted by employee', type: 'info' },
      { time: '10:31:50', message: 'Claim auto-categorized and sent to manager', type: 'success' },
    ],
  },
  {
    id: 'ezycabs',
    name: 'EzyCabs',
    icon: '🚕',
    status: 'active',
    description: 'Manages cab booking, ride scheduling, route optimization, and corporate transport services.',
    capabilities: ['Cab Booking', 'Ride Scheduling', 'Route Optimization', 'Fare Estimation'],
    tasksCompleted: 1876,
    avgResponseTime: '0.5s',
    successRate: '97.9%',
    model: 'GPT-4o',
    lastActive: '30s ago',
    logs: [
      { time: '10:32:00', message: 'Ride booked for employee pickup at Gate 3', type: 'success' },
      { time: '10:32:02', message: 'Driver assigned, ETA 5 minutes', type: 'info' },
    ],
  },
  {
    id: 'canteen-services',
    name: 'Canteen Services',
    icon: '🍽️',
    status: 'active',
    description: 'Manages cafeteria menus, meal pre-orders, dietary preferences, and nutritional information.',
    capabilities: ['Menu Display', 'Meal Pre-ordering', 'Dietary Tracking', 'Nutritional Info'],
    tasksCompleted: 3421,
    avgResponseTime: '0.4s',
    successRate: '99.8%',
    model: 'GPT-4o',
    lastActive: '2 min ago',
    logs: [
      { time: '10:30:00', message: 'Today\'s menu updated successfully', type: 'success' },
      { time: '10:31:30', message: '45 meal pre-orders confirmed for lunch', type: 'info' },
    ],
  },
  {
    id: 'facility-it-support',
    name: 'Facility & IT Support',
    icon: '🔧',
    status: 'active',
    description: 'Handles facility management requests, IT support tickets, and infrastructure maintenance.',
    capabilities: ['Ticket Management', 'IT Support', 'Facility Requests', 'SLA Tracking'],
    tasksCompleted: 1234,
    avgResponseTime: '1.2s',
    successRate: '96.5%',
    model: 'GPT-4o',
    lastActive: '5 min ago',
    logs: [
      { time: '10:25:00', message: 'IT ticket IT-2025-4521 resolved', type: 'success' },
      { time: '10:28:00', message: 'New facility request received', type: 'info' },
    ],
  },
  {
    id: 'employee-onboarding',
    name: 'Employee Guided Onboarding',
    icon: '👤',
    status: 'active',
    description: 'Guides new employees through the onboarding process, documentation, and initial setup.',
    capabilities: ['Onboarding Workflow', 'Document Collection', 'Training Assignment', 'Access Provisioning'],
    tasksCompleted: 456,
    avgResponseTime: '1.5s',
    successRate: '98.2%',
    model: 'GPT-4o',
    lastActive: '10 min ago',
    logs: [
      { time: '10:20:00', message: '3 new joiners onboarding initiated', type: 'info' },
      { time: '10:22:00', message: 'Access credentials provisioned', type: 'success' },
    ],
  },
  {
    id: 'visitor-management',
    name: 'Visitor Management',
    icon: '🏢',
    status: 'idle',
    description: 'Manages visitor registration, gate passes, and corporate access control.',
    capabilities: ['Visitor Registration', 'Gate Pass Generation', 'Host Notification', 'Access Tracking'],
    tasksCompleted: 789,
    avgResponseTime: '0.9s',
    successRate: '99.0%',
    model: 'GPT-4o',
    lastActive: '30 min ago',
    logs: [
      { time: '10:00:00', message: 'Visitor pass VP-4521 generated', type: 'success' },
    ],
  },
  {
    id: 'retiral-benefits',
    name: 'Retiral Benefits',
    icon: '🏦',
    status: 'idle',
    description: 'Provides information on PF, gratuity, pension, and other retiral benefits.',
    capabilities: ['PF Balance', 'Gratuity Calculation', 'Pension Planning', 'Benefits Overview'],
    tasksCompleted: 321,
    avgResponseTime: '1.1s',
    successRate: '99.3%',
    model: 'GPT-4o',
    lastActive: '1 hr ago',
    logs: [
      { time: '09:30:00', message: 'PF statement generated for Q1', type: 'success' },
    ],
  },
  {
    id: 'triage-process',
    name: 'Triage Process',
    icon: '🎯',
    status: 'active',
    description: 'Classifies and routes incoming requests to the appropriate specialized agents.',
    capabilities: ['Request Classification', 'Priority Assignment', 'Agent Routing', 'SLA Management'],
    tasksCompleted: 5678,
    avgResponseTime: '0.3s',
    successRate: '97.1%',
    model: 'GPT-4o',
    lastActive: 'Just now',
    logs: [
      { time: '10:32:00', message: 'Request routed to Internet Reimbursement', type: 'info' },
      { time: '10:31:55', message: 'Priority classified: Medium', type: 'info' },
    ],
  },
];

export const initialConversations = [];

export const initialWorkflows = [
  { id: 1, name: 'Customer Support Pipeline', steps: 4, status: 'active', lastRun: '2 min ago', agents: ['Triage Process', 'EzyClaim', 'Facility & IT Support'] },
  { id: 2, name: 'Expense Processing Flow', steps: 3, status: 'idle', lastRun: '1 hr ago', agents: ['EzyClaim', 'Internet Reimbursement'] },
  { id: 3, name: 'Employee Onboarding', steps: 5, status: 'active', lastRun: '5 min ago', agents: ['Employee Guided Onboarding', 'Facility & IT Support', 'Governance & Security'] },
  { id: 4, name: 'Visitor Check-In', steps: 3, status: 'error', lastRun: '30 min ago', agents: ['Visitor Management', 'Governance & Security'] },
];

export const initialNotifications = [];
